import { FilterX } from 'lucide-react';
import {
  hasActiveRoutineFilters,
  NO_ROUTINE_FILTERS,
  offersAChoice,
  type FilterOption,
  type RoutineFilterState,
} from '../../../lib/routine-filters';
import { useTranslation } from '../../../lib/i18n';
import { Button, OverflowMenu, type OverflowMenuAction } from '../ui';
import styles from './RoutinesFilterBar.module.css';

// The Routines list's filter row: **Space, then Agent** — each a labelled DS picker whose label states
// the current value — plus a Clear that appears once something is narrowed. Deliberately the same shape
// and the same order as `TasksFilterBar`: Ongoing Tasks and Routines are the same screen over different
// rows, and a user who learns one has learned the other.
//
// The options are **passed in, not derived here**: the host clamps its state against the same two lists
// (`clampRoutineFilters`), and a list derived twice is a list that can disagree with itself. What the bar
// owns is the rule that a control with nothing to choose between is not drawn at all (`offersAChoice`).
export function RoutinesFilterBar({
  spaces,
  agents,
  filters,
  onChange,
  onBi,
  className,
}: {
  /** The Spaces worth offering — `spacesOf(routines, workspaces)`. Empty when the host fixes this axis. */
  spaces: FilterOption[];
  /** The agents worth offering — `agentsOf(routines, team)`. Empty when the host fixes this axis. */
  agents: FilterOption[];
  filters: RoutineFilterState;
  onChange: (next: RoutineFilterState) => void;
  /** Reports which filter was used, for BI. */
  onBi?: (name: string) => void;
  /** Layout from the enclosing row — the bar owns its chips, not its place on the page. */
  className?: string;
}) {
  const { t } = useTranslation();

  const pick = (name: string, next: RoutineFilterState) => {
    onBi?.(name);
    onChange(next);
  };

  const spaceActions: OverflowMenuAction[] = [
    {
      key: 'all',
      label: t('routines.filter.allSpaces'),
      onSelect: () => pick('filter_space_all', { ...filters, workspaceId: '' }),
    },
    ...spaces.map((space) => ({
      key: space.id,
      label: space.name,
      onSelect: () => pick('filter_space', { ...filters, workspaceId: space.id }),
    })),
  ];

  const agentActions: OverflowMenuAction[] = [
    {
      key: 'all',
      label: t('routines.filter.allAgents'),
      onSelect: () => pick('filter_agent_all', { ...filters, agentId: '' }),
    },
    ...agents.map((agent) => ({
      key: agent.id,
      label: agent.name,
      onSelect: () => pick('filter_agent', { ...filters, agentId: agent.id }),
    })),
  ];

  const spaceLabel = spaces.find((space) => space.id === filters.workspaceId)?.name ?? t('routines.filter.allSpaces');
  const agentLabel = agents.find((agent) => agent.id === filters.agentId)?.name ?? t('routines.filter.allAgents');

  return (
    <div className={className ? `${styles.bar} ${className}` : styles.bar}>
      {offersAChoice(spaces) && (
        <OverflowMenu
          label={spaceLabel}
          ariaLabel={t('packs.section.workspace')}
          actions={spaceActions}
          selectedKey={filters.workspaceId || 'all'}
          onOpen={() => onBi?.('filter_space_open')}
        />
      )}
      {offersAChoice(agents) && (
        <OverflowMenu
          label={agentLabel}
          ariaLabel={t('island.agent')}
          actions={agentActions}
          selectedKey={filters.agentId || 'all'}
          onOpen={() => onBi?.('filter_agent_open')}
        />
      )}
      {hasActiveRoutineFilters(filters) && (
        <Button
          variant="text"
          size="small"
          startIcon={<FilterX size={16} aria-hidden />}
          onClick={() => pick('filter_clear', NO_ROUTINE_FILTERS)}
        >
          {t('files.filter.clear')}
        </Button>
      )}
    </div>
  );
}
